/**
 * The shared garment mockup renderer. FR-03.
 *
 * SVG paths, the shade() colour maths and the per-garment print zones are
 * lifted verbatim from the approved prototype (src/main.js). Constitution §6
 * makes these the approved UX: changing a path or a zone is a spec change, not
 * a judgement call here.
 *
 * The design overlay is positioned from {pos_x, pos_y, scale} percentages, the
 * identical convention the Flutter app uses (plan 001 §6). pos_x and pos_y are
 * percentages of the print zone, scale is a percentage of the zone's width, so
 * web and app render the same three numbers to the same picture at any size.
 *
 * This is a Server Component: the storefront renders mockups with no client JS.
 */

import type { GarmentType, Placement } from '@/lib/types';

/** Lighten or darken a hex colour by a flat channel amount. Prototype parity. */
export function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const cl = (c: number) => Math.max(0, Math.min(255, c));
  const r = cl((n >> 16) + amt);
  const g = cl(((n >> 8) & 255) + amt);
  const b = cl((n & 255) + amt);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export interface PrintZone {
  /** All four values are percentages of the mockup box. */
  x: number;
  y: number;
  w: number;
  h: number;
}

interface GarmentDef {
  zone: PrintZone;
  paths: (c: string) => React.ReactNode;
}

export const GARMENTS: Record<GarmentType, GarmentDef> = {
  tee: {
    zone: { x: 33, y: 31, w: 34, h: 34 },
    paths: (c) => (
      <>
        <path
          d="M60 32 L82 22 Q100 38 118 22 L140 32 L178 58 L160 86 L141 73 L141 184 L59 184 L59 73 L40 86 L22 58 Z"
          fill={c}
          stroke={shade(c, -28)}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M82 22 Q100 46 118 22" fill="none" stroke={shade(c, -28)} strokeWidth="2" />
      </>
    ),
  },
  polo: {
    zone: { x: 34, y: 38, w: 32, h: 30 },
    paths: (c) => (
      <>
        <path
          d="M60 34 L80 26 L100 44 L120 26 L140 34 L176 58 L159 84 L142 72 L142 184 L58 184 L58 72 L41 84 L24 58 Z"
          fill={c}
          stroke={shade(c, -28)}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M80 26 L100 44 L82 60 Z" fill={shade(c, 12)} stroke={shade(c, -28)} strokeWidth="2" />
        <path d="M120 26 L100 44 L118 60 Z" fill={shade(c, 12)} stroke={shade(c, -28)} strokeWidth="2" />
        <line x1="100" y1="44" x2="100" y2="78" stroke={shade(c, -28)} strokeWidth="2" />
        <circle cx="100" cy="56" r="2.2" fill={shade(c, -28)} />
        <circle cx="100" cy="70" r="2.2" fill={shade(c, -28)} />
      </>
    ),
  },
  hoodie: {
    zone: { x: 34, y: 40, w: 32, h: 28 },
    paths: (c) => (
      <>
        <path
          d="M58 40 L82 28 Q100 16 118 28 L142 40 L180 66 L162 92 L143 80 L143 186 L57 186 L57 80 L38 92 L20 66 Z"
          fill={c}
          stroke={shade(c, -28)}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M82 28 Q100 60 118 28 Q112 44 100 46 Q88 44 82 28 Z"
          fill={shade(c, -14)}
          stroke={shade(c, -28)}
          strokeWidth="2"
        />
        <path
          d="M70 120 Q100 134 130 120 L130 150 Q100 160 70 150 Z"
          fill="none"
          stroke={shade(c, -28)}
          strokeWidth="2"
        />
        <line x1="92" y1="44" x2="88" y2="78" stroke={shade(c, -28)} strokeWidth="2" />
        <line x1="108" y1="44" x2="112" y2="78" stroke={shade(c, -28)} strokeWidth="2" />
      </>
    ),
  },
  cap: {
    zone: { x: 35, y: 44, w: 30, h: 20 },
    paths: (c) => (
      <>
        <path
          d="M40 116 Q44 60 100 58 Q156 60 160 116 Q100 104 40 116 Z"
          fill={c}
          stroke={shade(c, -28)}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path
          d="M36 116 Q100 100 164 116 Q176 146 150 150 Q100 138 50 150 Q24 146 36 116 Z"
          fill={shade(c, -12)}
          stroke={shade(c, -28)}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <circle cx="100" cy="60" r="4" fill={shade(c, -20)} />
      </>
    ),
  },
  tote: {
    zone: { x: 32, y: 36, w: 36, h: 40 },
    paths: (c) => (
      <>
        <path
          d="M64 52 Q64 24 100 24 Q136 24 136 52"
          fill="none"
          stroke={shade(c, -30)}
          strokeWidth="6"
        />
        <rect
          x="48"
          y="50"
          width="104"
          height="128"
          rx="6"
          fill={c}
          stroke={shade(c, -28)}
          strokeWidth="2"
        />
      </>
    ),
  },
  /**
   * Everything that is not one of the five.
   *
   * 380 of this client's 381 products are `other`: leggings, crop tops,
   * shorts, sports bras. Until now the union did not admit the value, so the
   * lookup fell through to `tee` and the whole catalogue was drawn as a
   * t-shirt on the stock screen and on every order. R-251.
   *
   * A plain panel, not a guessed garment. It carries the print zone and says
   * nothing it does not know, which is the honest answer to "we have not been
   * told what shape this is".
   */
  other: {
    zone: { x: 30, y: 30, w: 40, h: 40 },
    paths: (c) => (
      <>
        <rect
          x="42"
          y="30"
          width="116"
          height="148"
          rx="10"
          fill={c}
          stroke={shade(c, -28)}
          strokeWidth="2"
        />
        {/* A seam, so the panel reads as a garment rather than as a swatch. */}
        <path
          d="M42 52 L158 52"
          fill="none"
          stroke={shade(c, -28)}
          strokeWidth="2"
          strokeDasharray="4 5"
        />
      </>
    ),
  },
};

export const DEFAULT_PLACEMENT: Placement = { pos_x: 50, pos_y: 48, scale: 60 };

/**
 * Overlay geometry, in percentages of the mockup box. Shared by this renderer
 * and the studio's drag handling so what you drag is what gets stored.
 */
export function overlayBox(zone: PrintZone, placement: Placement) {
  const size = (placement.scale / 100) * zone.w; // % of box width
  const cx = zone.x + (placement.pos_x / 100) * zone.w;
  const cy = zone.y + (placement.pos_y / 100) * zone.h;
  return { size, cx, cy };
}

export interface GarmentMockupProps {
  type: GarmentType;
  colorHex: string;
  designUrl?: string | null;
  placement?: Placement | null;
  /** Draws the dashed print-area guide. Studio and admin publish preview only. */
  showZone?: boolean;
  className?: string;
  /** Describes the garment for assistive tech. */
  label: string;
  priority?: boolean;
}

export function GarmentMockup({
  type,
  colorHex,
  designUrl,
  placement,
  showZone = false,
  className,
  label,
}: GarmentMockupProps) {
  const garment = GARMENTS[type] ?? GARMENTS.tee;
  const zone = garment.zone;
  const place = placement ?? DEFAULT_PLACEMENT;
  const { size, cx, cy } = overlayBox(zone, place);

  return (
    <div className={className} role="img" aria-label={label}>
      <div className="relative h-full w-full">
        <svg
          viewBox="0 0 200 200"
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
          focusable="false"
        >
          {garment.paths(colorHex)}
        </svg>

        {showZone && (
          <div
            aria-hidden="true"
            className="absolute rounded-[3px] border border-dashed border-ink/25"
            style={{
              left: `${zone.x}%`,
              top: `${zone.y}%`,
              width: `${zone.w}%`,
              height: `${zone.h}%`,
            }}
          />
        )}

        {designUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={designUrl}
            alt=""
            aria-hidden="true"
            className="absolute select-none object-contain"
            style={{
              // The prototype sizes the overlay as a square box of `scale`% of
              // the zone width and centres it on (cx, cy). The mockup box is
              // square, so the same percentage works on both axes.
              width: `${size}%`,
              height: `${size}%`,
              left: `${cx}%`,
              top: `${cy}%`,
              transform: 'translate(-50%, -50%)',
              filter: 'drop-shadow(0 2px 4px rgb(0 0 0 / 0.18))',
            }}
          />
        )}
      </div>
    </div>
  );
}
